// src/components/runs/RunFormSlideOver.jsx
import React, { useEffect, useState } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const API_BASE_URL = 'https://patrol-visit.patroll-management.nissiindia.com';

const defaultItem = {
  client_id: '',
  property_id: '',
  window_start: '',
  window_end: '',
  delivery_mode: 'auto',
  // used only on frontend; we convert it to checklist_json before submit
  selectedChecklistIds: [],
};

const RunFormSlideOver = ({
  isOpen,
  editingRun,
  saving,
  onClose,
  onSubmit,
}) => {
  const { theme } = useTheme();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      run_date: '',
      guard_id: '',
      upload_mode: 'auto',
      title: '',
      notes: '',
      start_time: '',
      end_time: '',
      items: [defaultItem],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const itemsWatch = watch('items');
  const navigate = useNavigate();

  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');

  // Lookups
  const [guards, setGuards] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

  // Fetch lookups when form opens
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setLoadingLookups(true);
        const token = getAuthToken();
        const role = getAuthRole();

        // No token or not admin → send to login
        if (!token || role !== 'admin') {
          toast.error('Session expired. Please log in again.');
          logoutAndRedirect();
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/runs/lookups`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.message || 'Failed to load lookup data');
        }

        // 🔹 Filter guards: exclude inactive or deleted
        const rawGuards = json.guards || [];
        const activeGuards = rawGuards.filter((g) => {
          const status = (g.status || '').toString().toLowerCase();
          const isDeleted = Number(g.is_deleted) === 1;
          // keep only if NOT inactive and NOT deleted
          return status !== 'inactive' && !isDeleted;
        });

        setGuards(activeGuards);
        setClients(json.clients || []);
        setProperties(json.properties || []);
      } catch (err) {
        console.error('[RunFormSlideOver] fetchLookups error', err);
      } finally {
        setLoadingLookups(false);
      }
    };

    if (isOpen) {
      fetchLookups();
    }
  }, [isOpen]);

  // When editing, populate form
  useEffect(() => {
    if (editingRun) {
      reset({
        run_date: editingRun.run_date || '',
        guard_id: editingRun.guard_id || '',
        upload_mode: editingRun.upload_mode || 'auto',
        title: editingRun.title || '',
        notes: editingRun.notes || '',
        start_time: editingRun.start_time || '',
        end_time: editingRun.end_time || '',
        // For now, items editing is limited; we just show a single placeholder item
        items: [defaultItem],
      });
    } else {
      reset({
        run_date: '',
        guard_id: '',
        upload_mode: 'auto',
        title: '',
        notes: '',
        start_time: '',
        end_time: '',
        items: [defaultItem],
      });
    }
  }, [editingRun, reset]);

  // Build checklist_json before passing to parent
  const handleInternalSubmit = (values) => {
    const transformedItems = (values.items || []).map((item) => {
      const property = properties.find(
        (p) => String(p.id) === String(item.property_id)
      );

      const selectedIds = item.selectedChecklistIds || [];
      let checklistPayload = null;

      if (property && property.checklist_json) {
        let originalList = [];
        try {
          originalList = JSON.parse(property.checklist_json);
        } catch (e) {
          console.error('Failed to parse property checklist_json', e);
        }

        const selectedItems = originalList.filter((c) =>
          selectedIds.includes(c.id)
        );

        if (selectedItems.length > 0) {
          checklistPayload = JSON.stringify(selectedItems);
        }
      }

      return {
        ...item,
        checklist_json: checklistPayload,
      };
    });

    const payload = {
      ...values,
      items: transformedItems,
    };

    onSubmit(payload, { editingRun });
  };

  const handleChecklistToggle = (itemIndex, checklistId) => {
    const path = `items.${itemIndex}.selectedChecklistIds`;
    const current = watch(path) || [];
    let updated;

    if (current.includes(checklistId)) {
      updated = current.filter((id) => id !== checklistId);
    } else {
      updated = [...current, checklistId];
    }

    setValue(path, updated, { shouldDirty: true });
  };

  if (!isOpen) return null;

  // Safe theme fallbacks
  const primary500 = theme?.primary?.[500] || '#6366f1';
  const primary600 = theme?.primary?.[600] || '#4f46e5';
  const secondary600 = theme?.secondary?.[600] || '#ec4899';

  return (
    <section className="mt-4">
      {/* Back to list */}
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => !saving && onClose()}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to runs
        </button>
      </div>

      {/* Main container card */}
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-xl">
        {/* Hero header */}
        <div
          className="border-b border-slate-800/40 px-6 py-5"
          style={{
            background: `linear-gradient(135deg, ${primary600} 0%, ${secondary600} 100%)`,
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">
                {editingRun ? 'Update schedule run' : 'Create schedule run'}
              </p>
              <h2 className="mt-1 text-xl font-bold text-white">
                {editingRun ? 'Edit patrol run' : 'New patrol run'}
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-white/85 max-w-2xl">
                Configure guard, schedule window, client properties and delivery
                mode. Guards will see this run in their app for the selected
                day.
              </p>
            </div>
            <button
              type="button"
              className="mt-1 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              onClick={() => !saving && onClose()}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Form content */}
        <form onSubmit={handleSubmit(handleInternalSubmit)}>
          <div className="space-y-6 px-6 py-6">
            {/* SECTION: Run basics */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold text-slate-800">
                Run details
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    Run Date <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...register('run_date', { required: true })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                    style={{
                      borderColor: 'rgb(226 232 240)',
                      '--ring': `${primary500}33`,
                    }}
                  />
                  {errors.run_date && (
                    <p className="mt-1 text-[11px] text-rose-500">
                      Run date is required.
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    Guard <span className="text-rose-500">*</span>
                  </label>
                  <select
                    {...register('guard_id', { required: true })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                    style={{
                      borderColor: 'rgb(226 232 240)',
                      '--ring': `${primary500}33`,
                    }}
                  >
                    <option value="">Select guard</option>
                    {guards.map((g) => (
                      <option key={g.id} value={g.id}>
                        {`${g.first_name || ''} ${g.last_name || ''}`.trim()}{' '}
                        {g.employee_code ? `- ${g.employee_code}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Only active, non-deleted guards are shown.
                  </p>
                  {errors.guard_id && (
                    <p className="mt-1 text-[11px] text-rose-500">
                      Please select a guard.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    Start Time
                  </label>
                  <input
                    type="time"
                    {...register('start_time')}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                    style={{
                      borderColor: 'rgb(226 232 240)',
                      '--ring': `${primary500}33`,
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    End Time
                  </label>
                  <input
                    type="time"
                    {...register('end_time')}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                    style={{
                      borderColor: 'rgb(226 232 240)',
                      '--ring': `${primary500}33`,
                    }}
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    Title
                  </label>
                  <input
                    type="text"
                    {...register('title')}
                    placeholder="Night Shift – Guard 3"
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                    style={{
                      borderColor: 'rgb(226 232 240)',
                      '--ring': `${primary500}33`,
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                    Upload Mode
                  </label>
                  <div className="mt-1 flex flex-wrap gap-3 text-[13px] text-slate-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="auto"
                        {...register('upload_mode')}
                        className="h-4 w-4"
                      />
                      Auto (send visit to client automatically)
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="manual"
                        {...register('upload_mode')}
                        className="h-4 w-4"
                      />
                      Manual (admin reviews before sending)
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Notes
                </label>
                <textarea
                  rows={3}
                  {...register('notes')}
                  placeholder="Any special instructions for this run..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                  style={{
                    borderColor: 'rgb(226 232 240)',
                    '--ring': `${primary500}33`,
                  }}
                />
              </div>
            </section>

            {/* SECTION: Run items */}
            {!editingRun && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Run items (properties to visit)
                    </h3>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Select the client, property, time window and checklist for
                      each stop in this run.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => append(defaultItem)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    + Add Property
                  </button>
                </div>

                {loadingLookups && (
                  <p className="mb-3 text-[11px] text-slate-500">
                    Loading guards, clients &amp; properties...
                  </p>
                )}

                <div className="space-y-4">
                  {fields.map((field, index) => {
                    const selectedClientId =
                      itemsWatch?.[index]?.client_id || '';
                    const selectedPropertyId =
                      itemsWatch?.[index]?.property_id || '';

                    const filteredProperties = properties.filter((p) =>
                      selectedClientId
                        ? String(p.client_id) === String(selectedClientId)
                        : true
                    );

                    const propertyForChecklist = properties.find(
                      (p) => String(p.id) === String(selectedPropertyId)
                    );

                    let checklistItems = [];
                    if (propertyForChecklist?.checklist_json) {
                      try {
                        checklistItems = JSON.parse(
                          propertyForChecklist.checklist_json
                        );
                      } catch (e) {
                        console.error('Invalid checklist_json', e);
                      }
                    }

                    const selectedChecklistIds =
                      itemsWatch?.[index]?.selectedChecklistIds || [];

                    return (
                      <div
                        key={field.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-700">
                            Property #{index + 1}
                          </span>
                          {fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-[11px] font-medium text-rose-600 hover:text-rose-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-[12px] font-medium text-slate-700">
                              Client
                            </label>
                            <select
                              {...register(`items.${index}.client_id`)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                              style={{
                                borderColor: 'rgb(226 232 240)',
                                '--ring': `${primary500}33`,
                              }}
                            >
                              <option value="">Select client</option>
                              {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.full_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-[12px] font-medium text-slate-700">
                              Property
                            </label>
                            <select
                              {...register(`items.${index}.property_id`)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                              style={{
                                borderColor: 'rgb(226 232 240)',
                                '--ring': `${primary500}33`,
                              }}
                            >
                              <option value="">Select property</option>
                              {filteredProperties.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.property_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-[12px] font-medium text-slate-700">
                              Window start
                            </label>
                            <input
                              type="datetime-local"
                              {...register(`items.${index}.window_start`)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                              style={{
                                borderColor: 'rgb(226 232 240)',
                                '--ring': `${primary500}33`,
                              }}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-[12px] font-medium text-slate-700">
                              Window end
                            </label>
                            <input
                              type="datetime-local"
                              {...register(`items.${index}.window_end`)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]"
                              style={{
                                borderColor: 'rgb(226 232 240)',
                                '--ring': `${primary500}33`,
                              }}
                            />
                          </div>
                        </div>

                       

                        {/* Checklist from property */}
                        {checklistItems.length > 0 && (
                          <div className="mt-3">
                            <label className="mb-1 block text-[12px] font-semibold text-slate-700">
                              Checklist items for this property
                            </label>
                            <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
                              <div className="space-y-1">
                                {checklistItems.map((ci) => (
                                  <label
                                    key={ci.id}
                                    className="flex items-center gap-2 text-[13px] text-slate-700"
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4"
                                      checked={selectedChecklistIds.includes(
                                        ci.id
                                      )}
                                      onChange={() =>
                                        handleChecklistToggle(index, ci.id)
                                      }
                                    />
                                    <span>{ci.item}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {editingRun && (
              <p className="text-[11px] text-slate-500">
                Editing of individual run items is not included in this form
                yet. Use the dedicated runs detail page or future UI to manage
                items.
              </p>
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-slate-200 bg-white px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => !saving && onClose()}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${primary600} 0%, ${secondary600} 100%)`,
                }}
              >
                {saving
                  ? editingRun
                    ? 'Saving...'
                    : 'Creating...'
                  : editingRun
                  ? 'Save Changes'
                  : 'Create Run'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default RunFormSlideOver;
